import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface OAuthAuthorizationContext {
  handle: string;
  tenant: string;
}

export interface OAuthConsentScope {
  name: string;
  label?: string;
  description?: string;
}

export interface OAuthConsentMetadata {
  clientName: string;
  scopes: OAuthConsentScope[];
  csrfNonce?: string;
}

export interface OAuthConsentCompletionResponse {
  redirectUrl: string;
}

const STORAGE_KEY_HANDLE = 'oauth_authz_handle';
const STORAGE_KEY_TENANT = 'oauth_authz_tenant';
const HANDLE_PARAM = 'handle';
const TENANT_PARAM = 'tenant';
const LEGACY_HANDLE_PARAM = 'oauth_handle';
const LEGACY_TENANT_PARAM = 'oauth_tenant';
const HANDLE_PATTERN = /^[0-9a-f]{32}$/i;
const TENANT_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;

@Injectable({
  providedIn: 'root',
})
export class OAuthAuthorizationService {
  constructor(private readonly _http: HttpClient) {}

  captureFromQueryParams(queryParams: Params): boolean {
    const handle = this.normalizeParam(
      queryParams[HANDLE_PARAM] ?? queryParams[LEGACY_HANDLE_PARAM]
    );
    const tenant = this.normalizeParam(
      queryParams[TENANT_PARAM] ?? queryParams[LEGACY_TENANT_PARAM]
    );

    if (!handle && !tenant) {
      return false;
    }

    if (!this.isValidHandle(handle) || !this.isValidTenant(tenant)) {
      this.clearContext();
      return false;
    }

    this.persistContext({ handle: handle!, tenant: tenant! });
    return true;
  }

  getStoredContext(): OAuthAuthorizationContext | null {
    const handle = sessionStorage.getItem(STORAGE_KEY_HANDLE);
    const tenant = sessionStorage.getItem(STORAGE_KEY_TENANT);

    if (!handle || !tenant) {
      return null;
    }

    if (!this.isValidHandle(handle) || !this.isValidTenant(tenant)) {
      this.clearContext();
      return null;
    }

    return { handle, tenant };
  }

  hasPendingAuthorization(): boolean {
    return this.getStoredContext() !== null;
  }

  clearContext(): void {
    sessionStorage.removeItem(STORAGE_KEY_HANDLE);
    sessionStorage.removeItem(STORAGE_KEY_TENANT);
  }

  getConsentMetadataUrl(context: OAuthAuthorizationContext): string {
    return this.buildConsentUrl(context, 'consent');
  }

  getApproveUrl(context: OAuthAuthorizationContext): string {
    return this.buildConsentUrl(context, 'consent/approve');
  }

  getDenyUrl(context: OAuthAuthorizationContext): string {
    return this.buildConsentUrl(context, 'consent/deny');
  }

  loadConsentMetadata(
    context: OAuthAuthorizationContext
  ): Observable<OAuthConsentMetadata> {
    return this._http.get<OAuthConsentMetadata>(
      this.getConsentMetadataUrl(context)
    );
  }

  approve(
    context: OAuthAuthorizationContext,
    csrfNonce?: string
  ): Observable<OAuthConsentCompletionResponse> {
    return this._http.post<OAuthConsentCompletionResponse>(
      this.getApproveUrl(context),
      csrfNonce ? { csrfNonce } : {}
    );
  }

  deny(
    context: OAuthAuthorizationContext,
    csrfNonce?: string
  ): Observable<OAuthConsentCompletionResponse> {
    return this._http.post<OAuthConsentCompletionResponse>(
      this.getDenyUrl(context),
      csrfNonce ? { csrfNonce } : {}
    );
  }

  completeWithRedirect(response: OAuthConsentCompletionResponse): void {
    if (!response?.redirectUrl) {
      throw new Error('Missing redirect URL from authorization server');
    }
    this.clearContext();
    window.location.href = response.redirectUrl;
  }

  isValidHandle(handle: string | null | undefined): boolean {
    return !!handle && HANDLE_PATTERN.test(handle);
  }

  isValidTenant(tenant: string | null | undefined): boolean {
    return !!tenant && TENANT_PATTERN.test(tenant);
  }

  private persistContext(context: OAuthAuthorizationContext): void {
    sessionStorage.setItem(STORAGE_KEY_HANDLE, context.handle);
    sessionStorage.setItem(STORAGE_KEY_TENANT, context.tenant);
  }

  private buildConsentUrl(
    context: OAuthAuthorizationContext,
    path: string
  ): string {
    const handle = encodeURIComponent(context.handle);
    // Tenant is resolved on the backend from TenantContext (defaults to MASTER); it is NOT a path
    // segment — the consent endpoints live at /public/oauth/consent[/approve|/deny].
    return `${environment.apiBase}/public/oauth/${path}?handle=${handle}`;
  }

  private normalizeParam(value: string | string[] | null | undefined): string | null {
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
}
